/**
 * MorePage - Mobile navigation menu for secondary pages
 *
 * Shows links to:
 * - Analytics
 * - Categories
 * - Settings
 * - Dev Tools (owner only)
 * - Logs (owner only)
 */

import { Link } from 'react-router-dom';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import { useAuth } from '@services/auth';
import './MorePage.css';

interface MenuItemProps {
  to: string;
  icon: string;
  label: string;
  description: string;
}

function MenuItem({ to, icon, label, description }: MenuItemProps) {
  return (
    <Link to={to} className="more-menu-item">
      <span className="more-menu-icon">{icon}</span>
      <div className="more-menu-text">
        <span className="more-menu-label">{label}</span>
        <span className="more-menu-desc">{description}</span>
      </div>
      <span className="more-menu-arrow">›</span>
    </Link>
  );
}

export default function MorePage() {
  const { isOwner } = useOwnerAccess();
  const { user } = useAuth();

  return (
    <div className="more-page">
      <header className="more-header">
        <h1 className="more-title">More</h1>
        {user && (
          <p className="more-user">{user.email}</p>
        )}
      </header>

      <div className="more-menu">
        <MenuItem
          to="/analytics"
          icon="📈"
          label="Analytics"
          description="Charts, trends, and insights"
        />
        <MenuItem
          to="/categories"
          icon="🏷️"
          label="Categories"
          description="Manage income & expense categories"
        />
        <MenuItem
          to="/import"
          icon="📥"
          label="Import Data"
          description="Import from bank, CSV, or other apps"
        />
        <MenuItem
          to="/settings"
          icon="⚙️"
          label="Settings"
          description="Theme, export, account"
        />

        {/* Owner-only items */}
        {isOwner && (
          <>
            <div className="more-divider">
              <span>Developer</span>
            </div>
            <MenuItem
              to="/dev"
              icon="🛠️"
              label="Dev Tools"
              description="Sync, database, debugging"
            />
            <MenuItem
              to="/metrics_dev"
              icon="M"
              label="Dev Metrics"
              description="Usage, activity, setup"
            />
            <MenuItem
              to="/log"
              icon="📜"
              label="Logs"
              description="Real-time log viewer"
            />
          </>
        )}
      </div>
    </div>
  );
}
